
import { useEffect, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import styled from 'styled-components';
import Button_v2 from '@/components/molecules/Button/index';
import Spinner from '@/components/molecules/Spinner';
import Title_v2 from '@/components/molecules/Title/index';
import { Svg } from '@/components/styled';
import { Pen } from '@/public/img/icons';
import { TitleProps } from '@/queries/components/molecules';
import devices from '@/utilities/devices';
import { renderUnderline } from '@/utilities/functions';

// #region STYLE
const SectionWithHubspotPostsContainer = styled.div`
	display: flex;
	align-item: baseline;
	justify-content: center;
	gap: 2.25rem;

	/* CSS specific to macOS devices */
	@media not all and (${devices.macOS}) {
		> div {
			margin: 0 1.1rem 0 1.1rem;
		}
	}

	/* CSS specific to iOS devices */
	@supports (${devices.iOS}) {
		> div {
			margin: 2rem 0 1rem;
		}
	}

	@media screen and (${devices.mobile}) {
		align-items: center;
		flex-direction: column;
		gap: 0;
		margin: 2rem 0 1rem;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		align-items: center;
		flex-direction: column;
		gap: 0;
		margin: 2rem 0 1rem;
	}
`;

const HubspotCard = styled.div<{ avatar: string }>`
	border-radius: 10px;
	box-shadow: 0px 0px 10px rgba(63, 6, 43, 0.1), 0px 0px 50px rgba(63, 6, 43, 0.03), 0px 0px 70px rgba(63, 6, 43, 0.02);
	cursor: default;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	min-height: 20.625rem;
	padding: 1.25rem;
	width: 20.9rem;
	transition: .25s;

	.content-container {
		display: flex;
		flex-direction: column;
		justify-content: center;

		.title-container {
			align-items: center;
			display: flex;
			flex-direction: column;

			.title {
				color: ${({ theme }) => theme.color.black};
				font-weight: bold;
				margin: 1rem 0 2rem;
				font-size: ${({ theme }) => theme.fontSize.LARGE};
				text-align: center;
				display: -webkit-box;
				-webkit-line-clamp: 3;
				-webkit-box-orient: vertical;
				overflow: hidden;
			}
		}

		.tags-container {
			align-items: center;
			display: flex;
			justify-content: center;
			flex-wrap: nowrap;
			.tag {
				border: 1px solid ${({ theme }) => theme.color.grey};
				border-radius: 20px;
				font-size: ${({ theme }) => theme.fontSize.XSMALL};
				margin: 0 .3rem;
				overflow: hidden;
				padding: 3px 8px;
				text-overflow: ellipsis;
				white-space: pre;
			}
		}

		.profile-and-date-container {
			align-items: center;
			display: flex;
			margin: 1.25rem 0;

			.profile {
				align-items: center;
				color: ${({ theme }) => theme.color.greyDarker};
				display: flex;
				font-size: ${({ theme }) => theme.fontSize.SMALL};
				margin: 0 .5rem;
				width: 60%;

				.avatar {
					background-image: url(${({ avatar }) => avatar});
					background-position: center;
					background-repeat: no-repeat;
					background-size: cover;
					border-radius: 50%;
					flex-shrink: 0;
					margin-right: 7px;
					height: 22px;
					width: 22px;
				}

				.name {
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: pre;
				}
			}

			.date {
				align-items: center;
				color: ${({ theme }) => theme.color.greyDarker};
				display: flex;
				font-size: ${({ theme }) => theme.fontSize.SMALL};
				width: 40%;

				svg {
					padding-right: 5px;
					padding-bottom: 2px;
				}
			}
		}
	}

	:hover {
		transform: translateY(-3px);
		box-shadow: 0px 0px 20px rgba(63, 6, 43, 0.1), 0px 0px 60px rgba(63, 6, 43, 0.03), 0px 0px 80px rgba(63, 6, 43, 0.02);
	}

	@media screen and (${devices.mobile}) {
		margin-bottom: 2rem;
		width: 90%;
		.date {
			align-items: baseline !important;
			svg {
				padding-bottom: 0px !important;
			}
		}
		.button-container {
			> div {
				padding: 1rem 0 0;
				> a {
					margin: 0;
				}
			}
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		margin-bottom: 2rem;
		width: 20.9rem;
		.button-container {
			> div {
				padding: 1rem 0 0;
				> a {
					margin: 0;
				}
			}
		}
	}
`;
// #endregion

export enum POST_SECTOR_TYPE {
	hcr = 'hcr',
}

export interface TagCategory {
	[POST_SECTOR_TYPE.hcr]: string[]
}

export interface SectionWithHubspotPostProps {
	category: POST_SECTOR_TYPE,
	limit: number
	tagId: string;
	title: TitleProps
}

export interface PostProps {
	author: string;
	avatar: string;
	href: string;
	tags: number[];
	title: string;
	publishedAt: Date;
}

// #region COMPONENT
const SectionWithHubspotPosts = ({ sectionWithHubspotPostData }:
{ sectionWithHubspotPostData: SectionWithHubspotPostProps }) => {

	const [blogPosts, setBlogPosts] = useState<PostProps[] | null>(null);

	useEffect(
		() => {
			fetch(`/hubspot-posts?tagId=${sectionWithHubspotPostData.tagId}&limit=${sectionWithHubspotPostData.limit}`)
				.then(response => response.json())
				.then(posts => setBlogPosts(posts));
		},
		[sectionWithHubspotPostData.tagId, sectionWithHubspotPostData.limit],
	);

	const tags: TagCategory = {
		hcr: ['Hôtellerie & Restauration', 'Entreprise'],
	};

	if (!blogPosts) {
		return <> <Spinner /> </>;
	}

	return (
		<>
			<Title_v2 underlineColor={sectionWithHubspotPostData.title.underlineColor} padding='0rem 0 3rem 0'>
				{renderUnderline(sectionWithHubspotPostData.title.titlePieces)}
			</Title_v2>
			<SectionWithHubspotPostsContainer>
				{blogPosts.map(
					(blogPost: PostProps, index: number) => (
						<HubspotCard key={index} avatar={blogPost.avatar}>
							<div className='content-container'>
								<div className='title-container'>
									<Svg path='red'>
										<Pen />
									</Svg>
									<div className='title'>
										{blogPost.title}
									</div>
								</div>

								<div className='tags-container'>
									{tags[sectionWithHubspotPostData.category].map(
										(tag, tag_index) => (
											<div className='tag' key={tag_index}>
												{tag}
											</div>
										),
									)}
								</div>

								<div className='profile-and-date-container'>
									<div className='profile'>
										<div className='avatar' />
										<div className='name'> {blogPost.author} </div>
									</div>

									<div className='date'>
										<FiClock />
										{new Date(blogPost.publishedAt).toLocaleDateString('fr-FR', {
											day: 'numeric',
											month: 'short',
											year: 'numeric',
										})}
									</div>
								</div>
							</div>
							<div className='button-container'>
								<Button_v2
									href={blogPost.href}
									text="Lire l'article"
									openOnNewTab
									version='whiteOnRed'
								/>
							</div>
						</HubspotCard>
					),
				)}
			</SectionWithHubspotPostsContainer>
		</>
	);
};
// #endregion

export default SectionWithHubspotPosts;
